/* eslint-disable no-param-reassign */
import { createAction, createReducer } from "@reduxjs/toolkit"
import moment from "moment"
import _ from "lodash"
import { saveAs } from "file-saver"
import httpClient from "../../api/httpClient"
import { actions as notifier } from "../../component/notifier"

const LOADING = createAction("LOADING")
const GO_TO_VIEW = createAction("GO_TO_VIEW")
const DOC_ANALYTICS_VIEW_MODEL = createAction("DOC_ANALYTICS_VIEW_MODEL")
const RECENTLY_ADDED_DOC_LIST = createAction("RECENTLY_ADDED_DOC_LIST")
const NEED_ATTENTION_DOC_LIST = createAction("NEED_ATTENTION_DOC_LIST")
const FORMS_ACTION_NB = createAction("FORMS_ACTION_NB")
const SEARCH_DOC_LIST = createAction("SEARCH_DOC_LIST")
const CLEAR_DOC_LIST = createAction("CLEAR_DOC_LIST")
const GET_SIGN_DOCUMENT = createAction("GET_SIGN_DOCUMENT")
const ESIGN_VIEW = createAction("ESIGN_VIEW")

const MAIN_VIEW = 0
const SEARCH_VIEW = 1

function getLastUploadedDate(needYourAttentionDoc, recentlyAddedDoc) {
  const customSort = (acc, doc) => {
    const { updatedAt } = doc
    if (!acc) {
      return doc
    }
    if (!updatedAt) {
      return acc
    }
    if (moment(updatedAt).isAfter(acc.updatedAt)) {
      return doc
    }
    return acc
  }
  const firstDoc1 = needYourAttentionDoc.reduce(customSort, undefined)
  const firstDoc2 = recentlyAddedDoc.reduce(customSort, undefined)
  if (_.isUndefined(firstDoc1) && _.isUndefined(firstDoc2)) {
    return undefined
  }

  if (firstDoc1 && firstDoc2) {
    return firstDoc1.updatedAt < firstDoc2.updatedAt
      ? firstDoc2.updatedAt
      : firstDoc1.updatedAt
  }

  if (firstDoc1) {
    return firstDoc1.updatedAt
  }

  return firstDoc2.updatedAt
}

function fetchAnalytics() {
  return (dispatch, getState) => {
    const { documents } = getState()
    const { needYourAttentionDoc, recentlyAddedDoc } = documents
    const lastUploadDocumentDate = getLastUploadedDate(
      needYourAttentionDoc,
      recentlyAddedDoc
    )
    const recentsDocumentsCard = {
      subtitle: "",
      text: `${_.size(recentlyAddedDoc)}`,
      textSecondary: "",
      isRecentDoc: true
    }
    const lastDocumentCard = {
      subtitle: "",
      text:
        (lastUploadDocumentDate &&
          moment(lastUploadDocumentDate).format("L")) ||
        "N/A",
      isRecentDoc: false
    }

    return dispatch(
      DOC_ANALYTICS_VIEW_MODEL([recentsDocumentsCard, lastDocumentCard])
    )
  }
}

function fetchDocuments(args, cancelToken) {
  // build args from
  const queryArgs = _(args)
    .entries()
    .map((oneParam) => _.join(oneParam, "="))
    .join("&")

  const urlWithQuery = _.isEmpty(queryArgs)
    ? "/api/documents"
    : `/api/documents?${queryArgs}`

  return httpClient.get(urlWithQuery, { cancelToken }).then(({ data }) => data)
}

function search(searchParams) {
  return async (dispatch) => {
    dispatch(LOADING(true))
    try {
      const documents = await fetchDocuments(searchParams)
      dispatch(SEARCH_DOC_LIST({ searchParams, documents }))
    } finally {
      dispatch(LOADING(false))
    }
  }
}

function fetchNeedYourAttentionDocuments(cancelToken, params) {
  return async (dispatch) => {
    const documents = await fetchDocuments(
      {
        category: "forms",
        needAction: true,
        ...params // 'params' can contain an object overriding current parameters
      },
      cancelToken
    )
    dispatch(NEED_ATTENTION_DOC_LIST(documents))
  }
}

function fetchRecentlyAddedDocuments(cancelToken, params) {
  return async (dispatch) => {
    const fromDate = moment().subtract(1, "year").toISOString()
    // documents that do not need actions are explicitly removed,
    // because they are in upper table
    const documents = await fetchDocuments(
      {
        fromDate,
        needAction: false,
        ...params // 'params' can contain an object overriding current parameters
      },
      cancelToken
    )
    dispatch(RECENTLY_ADDED_DOC_LIST(documents))
  }
}

function fetchFormsActionNumber(cancelToken) {
  return async (dispatch) => {
    const documents = await fetchDocuments(
      { category: "forms", needAction: true },
      cancelToken
    )
    const formsNb = documents.length
    dispatch(FORMS_ACTION_NB(formsNb))
  }
}

function fetchDocumentsView(cancelToken, params) {
  return (dispatch) => {
    dispatch(LOADING(true))
    return Promise.all([
      dispatch(fetchNeedYourAttentionDocuments(cancelToken, params)),
      dispatch(fetchRecentlyAddedDocuments(cancelToken, params)),
      dispatch(fetchFormsActionNumber(cancelToken))
    ])
      .then(() => {
        dispatch(fetchAnalytics())
      })
      .finally(() => {
        dispatch(LOADING(false))
      })
  }
}

function fetchEsignView(formId, cancelToken) {
  return (dispatch) => {
    dispatch(LOADING(true))
    return httpClient
      .get(`/api/esign/view?formId=${formId}`, { cancelToken })
      .then(({ data }) => dispatch(ESIGN_VIEW(data)))
      .finally(() => dispatch(LOADING(false)))
  }
}

function fetchSignDocument(formId, cancelToken) {
  return (dispatch) => {
    dispatch(LOADING(true))
    return httpClient
      .get(`/api/forms/${formId}`, { cancelToken })
      .then(({ data }) => dispatch(GET_SIGN_DOCUMENT(data)))
      .finally(() => dispatch(LOADING(false)))
  }
}

function goToDocumentsSummary() {
  return (dispatch) => {
    dispatch(GO_TO_VIEW(MAIN_VIEW))
  }
}

function clearDocuments() {
  return (dispatch) => {
    dispatch(CLEAR_DOC_LIST())
  }
}

function download(doc) {
  return (dispatch) => {
    return httpClient
      .get(doc.url, { responseType: "arraybuffer" })
      .then((response) => {
        const binaryDocument = new Blob([response.data])
        const blobUrl = window.URL.createObjectURL(binaryDocument)
        saveAs(blobUrl, doc.name)
      })
      .catch((err) => dispatch(notifier.enqueueError(err.message)))
  }
}

function downloadZipArchive(paths) {
  return (dispatch) => {
    return httpClient
      .post("/api/file/download/zip", paths, { responseType: "arraybuffer" })
      .then((response) => {
        const binaryDocument = new Blob([response.data])
        const blobUrl = window.URL.createObjectURL(binaryDocument)
        saveAs(blobUrl, "Documents.zip")
      })
      .catch((err) => dispatch(notifier.enqueueError(err.message)))
  }
}

function deleteDocument(doc) {
  const { _id } = doc
  return (dispatch) => {
    dispatch(LOADING(true))
    return httpClient
      .delete(`/api/file/${_id}`)
      .finally(() => dispatch(LOADING(false)))
  }
}

const initialState = {
  loading: false,
  view: MAIN_VIEW,
  analytics: undefined,
  needYourAttentionDoc: undefined,
  recentlyAddedDoc: undefined,
  needYourAttentionForm: undefined,
  recentlyAddedForm: undefined,
  formsNb: 0,
  searchDoc: undefined,
  searchParams: undefined,
  signDocument: undefined,
  esignView: undefined
}

const reducer = createReducer(initialState, {
  LOADING: (state, { payload }) => {
    state.loading = payload
  },
  GO_TO_VIEW: (state, { payload }) => {
    state.view = payload

    if (payload === MAIN_VIEW) {
      state.searchParams = null
      state.searchDoc = null
    }
  },
  DOC_ANALYTICS_VIEW_MODEL: (state, { payload }) => {
    state.analytics = payload
  },
  NEED_ATTENTION_DOC_LIST: (state, { payload }) => {
    state.needYourAttentionDoc = payload
  },
  RECENTLY_ADDED_DOC_LIST: (state, { payload }) => {
    state.recentlyAddedDoc = payload
  },
  NEED_ATTENTION_FORM_LIST: (state, { payload }) => {
    state.needYourAttentionForm = payload
  },
  RECENTLY_ADDED_FORM_LIST: (state, { payload }) => {
    state.recentlyAddedForm = payload
  },
  FORMS_ACTION_NB: (state, { payload }) => {
    state.formsNb = payload
  },
  SEARCH_DOC_LIST: (state, { payload }) => {
    const { searchParams, documents } = payload
    state.searchParams = searchParams
    state.searchDoc = documents
    state.view = SEARCH_VIEW
  },
  CLEAR_DOC_LIST: () => {
    return initialState
  },
  GET_SIGN_DOCUMENT: (state, { payload }) => {
    state.signDocument = payload
  },
  ESIGN_VIEW: (state, { payload }) => {
    state.esignView = payload
  }
})

export default {
  initialState,
  actions: {
    fetchAnalytics,
    fetchDocumentsView,
    fetchSignDocument,
    fetchEsignView,
    search,
    download,
    downloadZipArchive,
    deleteDocument,
    goToDocumentsSummary,
    clearDocuments
  },
  reducer
}
